/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, ClipboardCheck, Paintbrush, PartyPopper } from "lucide-react";
import ChapterHeading from "./ChapterHeading";
import { fadeUp, staggerParent, viewportOnce } from "../../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { icon: MessageSquare, n: "1", title: "Entretien en boutique", line: "On vient vous rencontrer chez vous." },
  { icon: ClipboardCheck, n: "2", title: "État des lieux", line: "On évalue ensemble votre commerce." },
  { icon: Paintbrush, n: "3", title: "Transformation en 5–7 jours", line: "Enseigne, peinture, présentoir, outils." },
  { icon: PartyPopper, n: "4", title: "Réouverture avec le quartier", line: "Votre boutique, en mieux." },
];

const EMPTY = { nom: "", telephone: "", quartier: "", message: "" };

export default function DevenirDiallo() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.telephone.trim() || !form.quartier.trim()) {
      toast.error("Merci de renseigner votre nom, téléphone et quartier.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, form);
      toast.success("Merci ! Votre demande est bien reçue. On vous rappelle très vite. :-D", { duration: 6000 });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 text-diallo-ink placeholder:text-diallo-ink/40 outline-none transition-colors focus:border-diallo-red focus:ring-2 focus:ring-diallo-red/20";

  return (
    <section id="devenir-diallo" data-testid="devenir-diallo-section" className="relative bg-diallo-sand py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="max-w-3xl">
          <ChapterHeading number="07" kicker="Devenir Diallo" title="Rejoignez le réseau Diallo." />
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-6 text-lg md:text-xl leading-relaxed text-diallo-ink/80"
          >
            Le kit de transformation est offert à 100 %. Zéro droit d'entrée, zéro redevance. En échange :
            vendez les fruits & légumes du réseau, servez de point de collecte, et partagez vos ventes en toute
            transparence.
          </motion.p>
        </div>

        {/* Timeline */}
        <motion.div
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.n} variants={fadeUp} className="relative rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-diallo-red text-white font-display font-bold">
                    {s.n}
                  </span>
                  <Icon size={22} className="text-diallo-charcoal/70" />
                </div>
                <h3 className="mt-4 font-display font-bold text-lg text-diallo-charcoal">{s.title}</h3>
                <p className="mt-1 text-diallo-ink/70 text-sm">{s.line}</p>
                {i < steps.length - 1 && (
                  <span className="hidden lg:block absolute top-1/2 -right-3 h-2 w-2 rounded-full bg-diallo-red/40" />
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Form */}
        <div id="contact" className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-2 rounded-3xl bg-diallo-charcoal p-8 md:p-10 text-white flex flex-col justify-center"
          >
            <h3 className="font-display font-extrabold text-3xl md:text-4xl leading-tight">
              Votre quartier vous connaît déjà.
            </h3>
            <p className="mt-4 text-white/75 leading-relaxed">
              Laissez-nous vos coordonnées. Notre équipe terrain vous rappelle pour un premier échange, sans
              engagement.
            </p>
            <p className="mt-6 font-display italic text-xl text-white">« Le quartier me connaît. »</p>
          </motion.div>

          <motion.form
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            onSubmit={onSubmit}
            data-testid="partner-form"
            className="lg:col-span-3 rounded-3xl bg-white p-8 md:p-10 shadow-[0_12px_40px_rgb(0,0,0,0.06)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Nom complet</label>
                <input name="nom" value={form.nom} onChange={onChange} placeholder="Ex. Alpha Diallo" className={inputCls} data-testid="form-nom" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Téléphone</label>
                <input name="telephone" value={form.telephone} onChange={onChange} placeholder="Ex. +225 07 00 00 00 00" className={inputCls} data-testid="form-telephone" />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Quartier / Commune</label>
              <input name="quartier" value={form.quartier} onChange={onChange} placeholder="Ex. Yopougon, Abidjan" className={inputCls} data-testid="form-quartier" />
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-diallo-charcoal">Votre message (optionnel)</label>
              <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Parlez-nous de votre boutique…" className={`${inputCls} resize-none`} data-testid="form-message" />
            </div>
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              data-testid="form-submit-btn"
              className="mt-6 w-full rounded-xl bg-diallo-red px-8 py-4 font-semibold text-lg text-white shadow-lg shadow-diallo-red/20 transition-colors hover:bg-diallo-reddeep disabled:opacity-60"
            >
              {loading ? "Envoi en cours…" : "Je veux devenir Diallo"}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
